import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import ChatModal from '../../components/ChatModal/ChatModal';
import UpgradePromptModal from '../../components/UpgradePromptModal/UpgradePromptModal';
import LoginModal from '../../components/LoginModal/LoginModal';
import { useSubscription } from '../../context/SubscriptionContext';
import { useAuth } from '../../context/AuthContext';
import { useInquiry } from '../../context/InquiryContext';
import { getPlanConfig, shouldShowVerifiedBadge, canSendRFQ } from '../../utils/membershipUtils';
import { API_BASE_URL } from '../../config/api';
import Pagination from '../../components/Pagination/Pagination';
import './ProductDetailsPage.css';

const ProductDetailsPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { hasPermission, planName, planDisplayName, canViewContactsForRegion, getFeature, loading: subscriptionLoading } = useSubscription();
  const { isAuthenticated } = useAuth();
  const { addToInquiry, isInInquiry, removeFromInquiry } = useInquiry();
  const [activeTab, setActiveTab] = useState('overview');
  const [mainImage, setMainImage] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [otherSuppliers, setOtherSuppliers] = useState([]);
  const [showMobile, setShowMobile] = useState(false);
  const [isChatModalOpen, setIsChatModalOpen] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeFeature, setUpgradeFeature] = useState('');
  const [upgradeMessage, setUpgradeMessage] = useState('');
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [addedProducts, setAddedProducts] = useState({});

  // Check if logged-in user can view phone numbers based on their plan
  // Use the actual hasPermission function from SubscriptionContext for accurate check
  const hasContactPlanPermission = hasPermission('canViewBuyerContacts');

  // Handle Add To Quotation click for similar products
  const handleSimilarInquireClick = (item, e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isAuthenticated) {
      setShowLoginModal(true);
      return;
    }
    const itemSeller = item.seller || {};
    const productData = {
      id: item.id,
      image: item.image || 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=500',
      description: item.description || item.name,
      priceMin: item.priceMin || 0,
      priceMax: item.priceMax || 0,
      priceUnit: 'USD',
      pricePer: item.unit || 'Unit',
      moq: item.moq || '1 Unit',
      companyName: itemSeller.name || 'Unknown Seller',
      sellerId: itemSeller.userId || item.userId,
      sellerName: itemSeller.name || 'Unknown Seller',
      sellerAvatar: itemSeller.logo || 'https://via.placeholder.com/52',
      country: itemSeller.country || 'UAE',
      countryFlag: itemSeller.countryFlag || 'https://flagcdn.com/w80/ae.png'
    };
    if (isInInquiry(item.id)) {
      removeFromInquiry(item.id);
      setAddedProducts(prev => ({ ...prev, [item.id]: false }));
    } else {
      addToInquiry(productData);
      setAddedProducts(prev => ({ ...prev, [item.id]: true }));
      setTimeout(() => {
        setAddedProducts(prev => ({ ...prev, [item.id]: 'done' }));
      }, 2000);
    }
  };
  
  // Check if user can view this specific supplier's contact
  const canViewSupplierContact = (seller) => {
    // First check if user has the base permission
    if (!hasPermission('canViewBuyerContacts')) {
      return false;
    }
    
    const supplierCountry = seller?.country || '';
    const contactRegion = getFeature ? getFeature('buyerContactRegion') : null;
    
    // If region is 'all' or 'global', can view any contact
    if (contactRegion === 'all' || contactRegion === 'global') {
      return true;
    }
    
    // If region is 'gcc', check if supplier is from GCC country
    if (contactRegion === 'gcc') {
      return canViewContactsForRegion ? canViewContactsForRegion(supplierCountry) : false;
    }
    
    return true;
  };

  // Get phone display based on user's plan AND supplier's country (GCC filtering)
  // Basic: no contacts, GCC Business: only GCC suppliers, Global Enterprise: all suppliers
  const getPhoneDisplay = (seller) => {
    const actualPhone = seller?.phone || seller?.phoneNumber || seller?.mobile;
    
    if (!actualPhone) return 'N/A';
    
    // Use canViewSupplierContact for consistent logic
    if (canViewSupplierContact(seller)) {
      return actualPhone;
    }
    return '***-***-****';  // Masked for Basic users or non-GCC suppliers for GCC Business
  };

  // Handle Chat button with permission check
  const handleChatWithSupplier = () => {
    // First check if user is logged in
    if (!isAuthenticated) {
      setShowLoginModal(true);
      return;
    }
    
    // Wait for subscription to load before checking permission
    if (subscriptionLoading) return;
    
    if (!hasPermission('canLiveChat')) {
      setUpgradeFeature('Live Chat');
      setUpgradeMessage('');
      setShowUpgradeModal(true);
      return;
    }
    setIsChatModalOpen(true);
  };

  // Handle View Mobile button - check if LOGGED-IN USER has permission to receive direct calls
  // View mobile numbers based on plan and region:
  // Basic: ❌ cannot view any mobile numbers
  // GCC Business: ✅ can view mobile numbers for GCC region suppliers only
  // Global Enterprise: ✅ can view all mobile numbers
  const handleViewMobile = () => {
    // First check if user is logged in
    if (!isAuthenticated) {
      setShowLoginModal(true);
      return;
    }
    
    // Wait for subscription to load
    if (subscriptionLoading) {
      console.log('[ViewMobile] Still loading subscription...');
      return;
    }
    
    // Get the seller from product to check region
    const seller = product?.seller || product;
    
    // Debug logging
    console.log('[ViewMobile] planName:', planName);
    console.log('[ViewMobile] hasPermission(canViewBuyerContacts):', hasPermission('canViewBuyerContacts'));
    console.log('[ViewMobile] getFeature(buyerContactRegion):', getFeature ? getFeature('buyerContactRegion') : 'N/A');
    console.log('[ViewMobile] seller country:', seller?.country);
    console.log('[ViewMobile] canViewSupplierContact result:', canViewSupplierContact(seller));
    
    // Check using canViewSupplierContact which handles region-based logic
    // Basic: no access, GCC Business: GCC suppliers only, Global Enterprise: all
    if (!canViewSupplierContact(seller)) {
      // User doesn't have permission - show upgrade modal with appropriate message
      const contactRegion = getFeature ? getFeature('buyerContactRegion') : null;
      
      if (contactRegion === 'gcc') {
        // GCC user trying to view non-GCC supplier
        setUpgradeFeature('View Mobile Number');
        setUpgradeMessage('This supplier is outside the GCC region. Upgrade to Global Enterprise to view mobile numbers for all suppliers worldwide.');
      } else {
        // Basic user
        setUpgradeFeature('View Mobile Number');
        setUpgradeMessage('Upgrade to GCC Business to view supplier mobile numbers in GCC region, or Global Enterprise for worldwide access.');
      }
      setShowUpgradeModal(true);
      return;
    }
    
    setShowMobile(!showMobile);
  };

  // Handle Request for Inquire button click
  const handleRequestInquire = () => {
    // Check if user is logged in first
    if (!isAuthenticated) {
      setShowLoginModal(true);
      return;
    }
    
    // Check if user can send RFQ - all plans allowed
    if (!canSendRFQ(planName)) {
      setUpgradeFeature('Send RFQ');
      setUpgradeMessage('You cannot send RFQs. Please check your account permissions.');
      setShowUpgradeModal(true);
      return;
    }
    
    // Navigate to send inquire page with product and seller data
    navigate(`/send-inquire/${product?.id}`, { 
      state: { 
        product: {
          id: product?.id,
          image: mainImage,
          name: product?.name || product?.title,
          description: product?.name || product?.title,
          priceMin: product?.priceMin,
          priceMax: product?.priceMax,
          unit: product?.unit || product?.pricePer,
          moq: product?.moq,
          sellerId: product?.sellerId,
          userId: product?.userId,
          seller: product?.seller,
          user: product?.user
        },
        seller: {
          id: seller?.id,
          userId: seller?.userId || product?.userId,
          name: seller?.name || seller?.companyName,
          logo: seller?.logo,
          type: seller?.isGlobal ? 'Global' : 'Local',
          verified: seller?.isVerified,
          country: seller?.country,
          memberYears: seller?.memberYears,
          businessType: seller?.businessType
        }
      }
    });
  };

  // Fetch product by ID
  useEffect(() => {
    const fetchProduct = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${API_BASE_URL}/products/${id}`);
        if (response.ok) {
          const data = await response.json();
          setProduct(data);
          // Set main image from images array or fallback to main image
          let productImages = [];
          if (data.images) {
            try {
              productImages = typeof data.images === 'string' ? JSON.parse(data.images) : data.images;
            } catch (e) {
              console.error('Error parsing images:', e);
            }
          }
          if (productImages && productImages.length > 0 && productImages[0]) {
            setMainImage(productImages[0]);
          } else if (data.image) {
            setMainImage(data.image);
          }
          // Fetch related products and other suppliers
          fetchRelatedProducts(id);
          fetchOtherSuppliers(id);
        } else {
          // Use default product if not found
          setProduct(getDefaultProduct());
        }
      } catch (error) {
        console.error('Error fetching product:', error);
        setProduct(getDefaultProduct());
      } finally {
        setLoading(false);
      }
    };
    if (id) {
      fetchProduct();
    } else {
      setProduct(getDefaultProduct());
      setLoading(false);
    }
  }, [id]);

  // Fetch related products
  const fetchRelatedProducts = async (productId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/products/${productId}/related?limit=50`);
      if (response.ok) {
        const data = await response.json();
        setRelatedProducts(data);
      }
    } catch (error) {
      console.error('Error fetching related products:', error);
    }
  };

  // Fetch other suppliers
  const fetchOtherSuppliers = async (productId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/products/${productId}/other-suppliers?limit=10`);
      if (response.ok) {
        const data = await response.json();
        setOtherSuppliers(data);
      }
    } catch (error) {
      console.error('Error fetching other suppliers:', error);
    }
  };

  // Default product fallback
  const getDefaultProduct = () => ({
    title: 'Construction of 15 story commercial office building with modern amenities and parking facilities.',
    name: 'Construction of 15 story commercial office building with modern amenities and parking facilities.',
    priceMin: 2.5,
    priceMax: 3.2,
    priceUnit: 'USD',
    unit: 'Tons',
    moq: '15 Tons',
    description: 'We are one of the well-known organizations which are engaged in satisfying the valued clients with the most excellent quality of Polished Turmeric Finger.',
    image: 'https://api.builder.io/api/v1/image/assets/TEMP/cc275acecdde1b7682591d397cc142f71b5d3f92?width=800',
    seller: {
      name: 'Eiffage Construction',
      companyName: 'Eiffage Construction',
      logo: 'https://api.builder.io/api/v1/image/assets/TEMP/435ad5be8193012a0ce26a0fa29d39ebbf91e166?width=112',
      reviews: 0,
      products: 20,
      isGlobal: true,
      isVerified: true,
      country: 'New York, United State',
      memberYears: 1,
      businessType: 'Manufacture',
      address: 'Saudi Arabia'
    }
  });

  // Generate thumbnails from product images array or main image
  const getProductImages = () => {
    // First try to parse images JSON if it exists
    let parsedImages = [];
    if (product?.images) {
      try {
        parsedImages = typeof product.images === 'string' 
          ? JSON.parse(product.images) 
          : product.images;
      } catch (e) {
        console.error('Error parsing product images:', e);
      }
    }
    
    // If we have parsed images, use them
    if (parsedImages && parsedImages.length > 0) {
      return parsedImages.filter(Boolean);
    }
    
    // Fallback to main image repeated
    const mainImg = product?.image || 'https://api.builder.io/api/v1/image/assets/TEMP/1c71bdd709a90ef879ec030e5f88229d06ab1391?width=800';
    return [mainImg];
  };
  
  const thumbnails = getProductImages();

  // Get seller info from product
  const seller = product?.seller || product?.supplier || getDefaultProduct().seller;

  // Get actual reviews from seller data (from sellerReviews relation)
  const reviews = (seller?.sellerReviews || []).map(r => ({
    id: r.id,
    name: r.reviewerName || 'Anonymous',
    avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(r.reviewerName || 'A')}&background=random&size=48`,
    location: r.reviewerCompany || '',
    rating: r.rating || 5,
    date: r.createdAt ? new Date(r.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '',
    comment: r.comment || ''
  }));
  const reviewCount = reviews.length;

  const keyFeatures = [
    'Grade: 53 OPC',
    'Bag Weight: 50 kg',
    'Setting Time: Initial – 30 mins, Final – 600 mins',
    'High compressive strength',
    'Low heat of hydration',
    'Suitable for RCC works, bridges, and foundations'
  ];

  const recommendedUses = [
    'Residential and commercial buildings',
    'Roads and flyovers',
    'Dams and irrigation projects',
    'Precast concrete structures'
  ];

  const renderStars = () => (
    <svg width="136" height="24" viewBox="0 0 136 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M11.0489 2.92705C11.3483 2.00574 12.6517 2.00574 12.9511 2.92705L14.4697 7.60081C14.6035 8.01284 14.9875 8.2918 15.4207 8.2918H20.335C21.3037 8.2918 21.7065 9.53141 20.9228 10.1008L16.947 12.9894C16.5966 13.244 16.4499 13.6954 16.5838 14.1074L18.1024 18.7812C18.4017 19.7025 17.3472 20.4686 16.5635 19.8992L12.5878 17.0106C12.2373 16.756 11.7627 16.756 11.4122 17.0106L7.43648 19.8992C6.65276 20.4686 5.59828 19.7025 5.89763 18.7812L7.41623 14.1074C7.55011 13.6954 7.40345 13.244 7.05296 12.9894L3.07722 10.1008C2.29351 9.53141 2.69628 8.2918 3.66501 8.2918H8.57929C9.01252 8.2918 9.39647 8.01284 9.53035 7.60081L11.0489 2.92705Z" fill="#FFBD00"/>
      <path d="M39.0489 2.92705C39.3483 2.00574 40.6517 2.00574 40.9511 2.92705L42.4697 7.60081C42.6035 8.01284 42.9875 8.2918 43.4207 8.2918H48.335C49.3037 8.2918 49.7065 9.53141 48.9228 10.1008L44.947 12.9894C44.5966 13.244 44.4499 13.6954 44.5838 14.1074L46.1024 18.7812C46.4017 19.7025 45.3472 20.4686 44.5635 19.8992L40.5878 17.0106C40.2373 16.756 39.7627 16.756 39.4122 17.0106L35.4365 19.8992C34.6528 20.4686 33.5983 19.7025 33.8976 18.7812L35.4162 14.1074C35.5501 13.6954 35.4034 13.244 35.053 12.9894L31.0772 10.1008C30.2935 9.53141 30.6963 8.2918 31.665 8.2918H36.5793C37.0125 8.2918 37.3965 8.01284 37.5303 7.60081L39.0489 2.92705Z" fill="#FFBD00"/>
      <path d="M67.0489 2.92705C67.3483 2.00574 68.6517 2.00574 68.9511 2.92705L70.4697 7.60081C70.6035 8.01284 70.9875 8.2918 71.4207 8.2918H76.335C77.3037 8.2918 77.7065 9.53141 76.9228 10.1008L72.947 12.9894C72.5966 13.244 72.4499 13.6954 72.5838 14.1074L74.1024 18.7812C74.4017 19.7025 73.3472 20.4686 72.5635 19.8992L68.5878 17.0106C68.2373 16.756 67.7627 16.756 67.4122 17.0106L63.4365 19.8992C62.6528 20.4686 61.5983 19.7025 61.8976 18.7812L63.4162 14.1074C63.5501 13.6954 63.4034 13.244 63.053 12.9894L59.0772 10.1008C58.2935 9.53141 58.6963 8.2918 59.665 8.2918H64.5793C65.0125 8.2918 65.3965 8.01284 65.5303 7.60081L67.0489 2.92705Z" fill="#FFBD00"/>
      <path d="M95.0489 2.92705C95.3483 2.00574 96.6517 2.00574 96.9511 2.92705L98.4697 7.60081C98.6035 8.01284 98.9875 8.2918 99.4207 8.2918H104.335C105.304 8.2918 105.706 9.53141 104.923 10.1008L100.947 12.9894C100.597 13.244 100.45 13.6954 100.584 14.1074L102.102 18.7812C102.402 19.7025 101.347 20.4686 100.564 19.8992L96.5878 17.0106C96.2373 16.756 95.7627 16.756 95.4122 17.0106L91.4365 19.8992C90.6528 20.4686 89.5983 19.7025 89.8976 18.7812L91.4162 14.1074C91.5501 13.6954 91.4034 13.244 91.053 12.9894L87.0772 10.1008C86.2935 9.53141 86.6963 8.2918 87.665 8.2918H92.5793C93.0125 8.2918 93.3965 8.01284 93.5303 7.60081L95.0489 2.92705Z" fill="#FFBD00"/>
      <path d="M123.049 2.92705C123.348 2.00574 124.652 2.00574 124.951 2.92705L126.47 7.60081C126.604 8.01284 126.987 8.2918 127.421 8.2918H132.335C133.304 8.2918 133.706 9.53141 132.923 10.1008L128.947 12.9894C128.597 13.244 128.45 13.6954 128.584 14.1074L130.102 18.7812C130.402 19.7025 129.347 20.4686 128.564 19.8992L124.588 17.0106C124.237 16.756 123.763 16.756 123.412 17.0106L119.436 19.8992C118.653 20.4686 117.598 19.7025 117.898 18.7812L119.416 14.1074C119.55 13.6954 119.403 13.244 119.053 12.9894L115.077 10.1008C114.294 9.53141 114.696 8.2918 115.665 8.2918H120.579C121.013 8.2918 121.396 8.01284 121.53 7.60081L123.049 2.92705Z" fill="#FFBD00"/>
    </svg>
  );

  const renderReviewCard = (review) => (
    <div key={review.id} className="review-card">
      <div className="review-top-row">
        <img src={review.avatar} alt={review.name} className="review-avatar" />
        <div className="review-user-info">
          <h4 className="review-user-name">{review.name}</h4>
          <span className="review-user-location">{review.location}</span>
        </div>
      </div>
      <div className="review-bottom-section">
        <div className="review-divider"></div>
        <div className="review-rating-date-row">
          <div className="review-stars-row">
            <svg width="136" height="24" viewBox="0 0 136 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              {[0, 28, 56, 84, 112].map((offset, i) => (
                <path key={i} d={`M${11.0489 + offset} 2.92705C${11.3483 + offset} 2.00574 ${12.6517 + offset} 2.00574 ${12.9511 + offset} 2.92705L${14.4697 + offset} 7.60081C${14.6035 + offset} 8.01284 ${14.9875 + offset} 8.2918 ${15.4207 + offset} 8.2918H${20.335 + offset}C${21.3037 + offset} 8.2918 ${21.7065 + offset} 9.53141 ${20.9228 + offset} 10.1008L${16.947 + offset} 12.9894C${16.5966 + offset} 13.244 ${16.4499 + offset} 13.6954 ${16.5838 + offset} 14.1074L${18.1024 + offset} 18.7812C${18.4017 + offset} 19.7025 ${17.3472 + offset} 20.4686 ${16.5635 + offset} 19.8992L${12.5878 + offset} 17.0106C${12.2373 + offset} 16.756 ${11.7627 + offset} 16.756 ${11.4122 + offset} 17.0106L${7.43648 + offset} 19.8992C${6.65276 + offset} 20.4686 ${5.59828 + offset} 19.7025 ${5.89763 + offset} 18.7812L${7.41623 + offset} 14.1074C${7.55011 + offset} 13.6954 ${7.40345 + offset} 13.244 ${7.05296 + offset} 12.9894L${3.07722 + offset} 10.1008C${2.29351 + offset} 9.53141 ${2.69628 + offset} 8.2918 ${3.66501 + offset} 8.2918H${8.57929 + offset}C${9.01252 + offset} 8.2918 ${9.39647 + offset} 8.01284 ${9.53035 + offset} 7.60081L${11.0489 + offset} 2.92705Z`} fill={i < review.rating ? '#FFBD00' : '#D1D6DB'}/>
              ))}
            </svg>
            <span className="rating-number">0{review.rating}</span>
          </div>
          <span className="review-date">{review.date}</span>
        </div>
        {review.comment && (
          <p className="review-comment">{review.comment}</p>
        )}
      </div>
    </div>
  );

  return (
    <div className="product-details-page">
      <Header />
      
      <main className="product-details-main">
        <div className="product-details-container">
          {loading ? (
            <div style={{textAlign: 'center', padding: '60px 20px'}}>
              <p>Loading product details...</p>
            </div>
          ) : (
          <>
          {/* Breadcrumb */}
          <div className="product-breadcrumb">
            <Link to="/" className="breadcrumb-link">Home</Link>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M6 13L10 9L6 5" stroke="#505050" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span className="breadcrumb-link">{product?.category?.name || 'Building Materials'}</span>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M6 13L10 9L6 5" stroke="#505050" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span className="breadcrumb-link">{product?.subcategory?.name || 'Product'}</span>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M6 13L10 9L6 5" stroke="#505050" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span className="breadcrumb-current">Details</span>
          </div>

          {/* Main Product Section */}
          <div className="product-main-section">
            {/* Left Side - Product Images & Details */}
            <div className="product-left-section">
              <div className="product-images-info-wrapper">
                {/* Product Images */}
                <div className="product-images-section">
                  <div className="main-image-container">
                    <img src={mainImage || product?.image} alt={product?.name || product?.title} className="main-product-image" />
                  </div>
                  <div className="thumbnail-images">
                    {thumbnails.map((thumb, index) => (
                      <img 
                        key={index}
                        src={thumb} 
                        alt={`Product view ${index + 1}`} 
                        className="thumbnail-image"
                        onClick={() => setMainImage(thumb)}
                      />
                    ))}
                  </div>
                </div>

                {/* Product Info */}
                <div className="product-info-section">
                  <div className="product-header-info">
                    <h1 className="product-title">{product?.name || product?.title}</h1>
                    
                    <div className="product-price-section">
                      <div className="price-main">
                        <span className="price-value">${product?.priceMin} - ${product?.priceMax} USD</span>
                        <span className="price-per">/ {product?.unit || product?.pricePer || 'Tons'}</span>
                      </div>
                      <p className="product-moq">
                        <span className="moq-value">{product?.moq || 'N/A'}</span>
                        <span className="moq-label"> (MOQ)</span>
                      </p>
                    </div>
                  </div>

                  <p className="product-short-description">{product?.description}</p>

                  <div className="product-action-row">
                    <button className="bookmark-btn">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M22 0H11.5C11.1022 0 10.7206 0.158035 10.4393 0.43934C10.158 0.720644 10 1.10218 10 1.5V18C10.0001 18.1338 10.036 18.2652 10.1039 18.3805C10.1719 18.4958 10.2695 18.5908 10.3866 18.6557C10.5037 18.7206 10.6361 18.753 10.7697 18.7495C10.9033 18.746 11.0339 18.7068 11.1475 18.6356L16.75 15.1297L22.3525 18.6356C22.4661 18.7068 22.5967 18.746 22.7303 18.7495C22.8639 18.753 22.9963 18.7206 23.1134 18.6557C23.2305 18.5908 23.3281 18.4958 23.3961 18.3805C23.464 18.2652 23.4999 18.1338 23.5 18V1.5C23.5 1.10218 23.342 0.720644 23.0607 0.43934C22.7794 0.158035 22.3978 0 22 0ZM22 16.6472L17.1466 13.6144C17.0274 13.5399 16.8896 13.5004 16.7491 13.5004C16.6085 13.5004 16.4708 13.5399 16.3516 13.6144L11.5 16.6472V1.5H22V16.6472Z" fill="#969696"/>
                      </svg>
                    </button>
                    <button 
                      className="request-inquire-btn"
                      onClick={handleRequestInquire}
                    >
                      Request for Inquire
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Side - Supplier Card */}
            <div className="supplier-card">
              <div className="supplier-header">
                <Link to={`/seller/${seller.slug || seller.id}`} className="supplier-profile" style={{ textDecoration: 'none', color: 'inherit', cursor: 'pointer' }}>
                  <img src={seller.logo} alt={seller.name || seller.companyName} className="supplier-logo" />
                  <div className="supplier-name-section">
                    <h3 className="supplier-name">{seller.name || seller.companyName}</h3>
                    <span className="supplier-info-label">Supplier Info</span>
                  </div>
                </Link>
                
                <div className="supplier-stats">
                  <span className="stat-item">{reviewCount} Reviews</span>
                  <div className="stat-divider"></div>
                  <span className="stat-item">{seller.products || 20} Products</span>
                </div>
              </div>

              <div className="supplier-badges">
                <div className="supplier-badge-row">
                  {(() => {
                    const planConfig = getPlanConfig(seller.membershipPlan);
                    return (
                      <div className="supplier-global-badge" style={{ borderColor: planConfig.color }}>
                        <img 
                          src={planConfig.icon} 
                          alt={planConfig.label} 
                          className="supplier-badge-icon"
                        />
                        <span style={{ color: planConfig.color }}>{planConfig.label}</span>
                      </div>
                    );
                  })()}
                  {shouldShowVerifiedBadge(seller.membershipPlan) && (
                    <div className="supplier-verified-badge">
                      <svg width="118" height="30" viewBox="0 0 118 30" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M22 15C22 15.9323 20.7329 16.6871 20.5034 17.5472C20.2653 18.4377 20.9782 19.7221 20.528 20.5015C20.0705 21.2923 18.597 21.314 17.9562 21.9562C17.314 22.5984 17.2923 24.0705 16.5014 24.528C15.7221 24.9782 14.4377 24.2653 13.5472 24.5034C12.6871 24.7329 11.9337 26 11 26C10.0663 26 9.31291 24.7329 8.45276 24.5034C7.56231 24.2653 6.27788 24.9782 5.49856 24.528C4.70769 24.0705 4.68605 22.597 4.04382 21.9562C3.4016 21.3154 1.92954 21.2923 1.47205 20.5015C1.02177 19.7221 1.73472 18.4377 1.49659 17.5472C1.26712 16.6871 0 15.9338 0 15C0 14.0663 1.26712 13.3129 1.49659 12.4528C1.73472 11.5623 1.02177 10.2779 1.47205 9.49857C1.92954 8.7077 3.40305 8.68605 4.04382 8.04383C4.68605 7.40161 4.70769 5.92955 5.49856 5.47206C6.27788 5.02178 7.56231 5.73472 8.45276 5.49659C9.31291 5.26712 10.0663 4 11 4C11.9337 4 12.6871 5.26712 13.5472 5.49659C14.4377 5.73472 15.7221 5.02178 16.5014 5.47206C17.2923 5.92955 17.314 7.40305 17.9562 8.04383C18.5984 8.68461 20.0705 8.7077 20.528 9.49857C20.9782 10.2779 20.2653 11.5623 20.5034 12.4528C20.7329 13.3129 22 14.0663 22 15Z" fill="#29ABE2"/>
                        <path d="M16.5876 11.6015C16.1229 11.1368 15.3696 11.1368 14.9049 11.6015L10.0312 16.4751L8.00786 14.4518C7.54315 13.9871 6.7898 13.9871 6.32509 14.4518C5.86039 14.9165 5.86039 15.6698 6.32509 16.1345L9.14798 18.9574C9.39188 19.2013 9.71372 19.3168 10.0327 19.3052C10.3516 19.3168 10.6734 19.2013 10.9173 18.9574L16.5891 13.2857C17.0538 12.821 17.0538 12.0676 16.5891 11.6029L16.5876 11.6015Z" fill="white"/>
                        <text fill="#29ABE2" style={{ whiteSpace: 'pre' }} fontFamily="Poppins" fontSize="17" letterSpacing="0em"><tspan x="27" y="20.95">Verified</tspan></text>
                      </svg>
                    </div>
                  )}
                </div>
              </div>

              <div className="supplier-details">
                <div className="supplier-detail-row">
                  <div className="supplier-country-info">
                    <div className="supplier-country-flag">
                      <img 
                        src="https://api.builder.io/api/v1/image/assets/TEMP/417568b6eb0fd8d1e09618ea0296738b87ee2e2a?width=62" 
                        alt="Flag" 
                        className="supplier-flag-small"
                      />
                    </div>
                    <span className="supplier-country-text">
                      <span className="supplier-country-name">{seller.country} / </span>
                      <span className="supplier-member-years">0{seller.memberYears || 1} Year</span>
                    </span>
                  </div>
                </div>

                <div className="supplier-business-info">
                  <div className="business-info-item">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M14.5 5H12V6H14.5V9H11V10H12.5V11.071C12.0287 11.1927 11.6179 11.4821 11.3447 11.885C11.0715 12.2879 10.9546 12.7766 11.0159 13.2595C11.0773 13.7424 11.3126 14.1864 11.6779 14.5082C12.0431 14.83 12.5132 15.0075 13 15.0075C13.4868 15.0075 13.9569 14.83 14.3221 14.5082C14.6874 14.1864 14.9227 13.7424 14.9841 13.2595C15.0454 12.7766 14.9285 12.2879 14.6553 11.885C14.3821 11.4821 13.9713 11.1927 13.5 11.071V10H14.5C14.7651 9.9996 15.0192 9.89412 15.2067 9.70667C15.3941 9.51922 15.4996 9.26509 15.5 9V6C15.5 5.73478 15.3946 5.48043 15.2071 5.29289C15.0196 5.10536 14.7652 5 14.5 5ZM14 13C14 13.1978 13.9414 13.3911 13.8315 13.5556C13.7216 13.72 13.5654 13.8482 13.3827 13.9239C13.2 13.9996 12.9989 14.0194 12.8049 13.9808C12.6109 13.9422 12.4327 13.847 12.2929 13.7071C12.153 13.5673 12.0578 13.3891 12.0192 13.1951C11.9806 13.0011 12.0004 12.8 12.0761 12.6173C12.1518 12.4346 12.28 12.2784 12.4444 12.1685C12.6089 12.0586 12.8022 12 13 12C13.2651 12.0004 13.5192 12.1059 13.7067 12.2933C13.8941 12.4808 13.9996 12.7349 14 13ZM9.5 3H7V4H9.5V7H6V8H7.5V11.071C7.02867 11.1927 6.61791 11.4821 6.3447 11.885C6.07149 12.2879 5.9546 12.7766 6.01594 13.2595C6.07728 13.7424 6.31263 14.1864 6.67788 14.5082C7.04314 14.83 7.51321 15.0075 8 15.0075C8.48679 15.0075 8.95686 14.83 9.32212 14.5082C9.68737 14.1864 9.92272 13.7424 9.98406 13.2595C10.0454 12.7766 9.92851 12.2879 9.6553 11.885C9.3821 11.4821 8.97133 11.1927 8.5 11.071V8H9.5C9.76522 8 10.0196 7.89464 10.2071 7.70711C10.3946 7.51957 10.5 7.26522 10.5 7V4C10.5 3.73478 10.3946 3.48043 10.2071 3.29289C10.0196 3.10536 9.76522 3 9.5 3ZM9 13C9 13.1978 8.94135 13.3911 8.83147 13.5556C8.72159 13.72 8.56541 13.8482 8.38268 13.9239C8.19996 13.9996 7.99889 14.0194 7.80491 13.9808C7.61093 13.9422 7.43275 13.847 7.29289 13.7071C7.15304 13.5673 7.0578 13.3891 7.01921 13.1951C6.98063 13.0011 7.00043 12.8 7.07612 12.6173C7.15181 12.4346 7.27998 12.2784 7.44443 12.1685C7.60888 12.0586 7.80222 12 8 12C8.26509 12.0004 8.51922 12.1059 8.70667 12.2933C8.89412 12.4808 8.9996 12.7349 9 13ZM4.5 1H1.5C1.23478 1 0.98043 1.10536 0.792893 1.29289C0.605357 1.48043 0.5 1.73478 0.5 2V5C0.5 5.26522 0.605357 5.51957 0.792893 5.70711C0.98043 5.89464 1.23478 6 1.5 6H2.5V11.071C2.02867 11.1927 1.61791 11.4821 1.3447 11.885C1.07149 12.2879 0.954603 12.7766 1.01594 13.2595C1.07728 13.7424 1.31263 14.1864 1.67788 14.5082C2.04314 14.83 2.51321 15.0075 3 15.0075C3.48679 15.0075 3.95686 14.83 4.32212 14.5082C4.68737 14.1864 4.92272 13.7424 4.98406 13.2595C5.0454 12.7766 4.92851 12.2879 4.6553 11.885C4.38209 11.4821 3.97133 11.1927 3.5 11.071V6H4.5C4.76522 6 5.01957 5.89464 5.20711 5.70711C5.39464 5.51957 5.5 5.26522 5.5 5V2C5.5 1.73478 5.39464 1.48043 5.20711 1.29289C5.01957 1.10536 4.76522 1 4.5 1ZM4 13C4 13.1978 3.94135 13.3911 3.83147 13.5556C3.72159 13.72 3.56541 13.8482 3.38268 13.9239C3.19996 13.9996 2.99889 14.0194 2.80491 13.9808C2.61093 13.9422 2.43275 13.847 2.29289 13.7071C2.15304 13.5673 2.0578 13.3891 2.01921 13.1951C1.98063 13.0011 2.00043 12.8 2.07612 12.6173C2.15181 12.4346 2.27998 12.2784 2.44443 12.1685C2.60888 12.0586 2.80222 12 3 12C3.26522 12 3.51957 12.1054 3.70711 12.2929C3.89464 12.4804 4 12.7348 4 13ZM1.5 5V2H4.5L4.501 5H1.5Z" fill="#6D6D6D"/>
                    </svg>
                    <span>{seller.businessType || 'Manufacturer'}</span>
                  </div>
                  <div className="business-info-item">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M8 1C5.24 1 3 3.24 3 6C3 9.75 8 15 8 15C8 15 13 9.75 13 6C13 3.24 10.76 1 8 1ZM8 8C6.9 8 6 7.1 6 6C6 4.9 6.9 4 8 4C9.1 4 10 4.9 10 6C10 7.1 9.1 8 8 8Z" fill="#6D6D6D"/>
                    </svg>
                    <span>{seller.address || seller.country}</span>
                  </div>
                </div>
              </div>

              <div className="supplier-action-buttons">
                <button className="view-mobile-btn" onClick={handleViewMobile}>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M11.5 0H4.5C3.67157 0 3 0.671573 3 1.5V14.5C3 15.3284 3.67157 16 4.5 16H11.5C12.3284 16 13 15.3284 13 14.5V1.5C13 0.671573 12.3284 0 11.5 0ZM8 15C7.44772 15 7 14.5523 7 14C7 13.4477 7.44772 13 8 13C8.55228 13 9 13.4477 9 14C9 14.5523 8.55228 15 8 15ZM11 12H5V2H11V12Z" fill="currentColor"/>
                  </svg>
                  {showMobile ? getPhoneDisplay(seller) : 'View mobile'}
                </button>
                <button className="chat-supplier-btn" onClick={handleChatWithSupplier}>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M14 0H2C0.9 0 0 0.9 0 2V10C0 11.1 0.9 12 2 12H4V15.5L9.5 12H14C15.1 12 16 11.1 16 10V2C16 0.9 15.1 0 14 0ZM14 10H9L6 12V10H2V2H14V10Z" fill="currentColor"/>
                  </svg>
                  Chat with supplier
                </button>
              </div>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="product-tabs">
            <button 
              className={`tab-btn ${activeTab === 'overview' ? 'active' : ''}`}
              onClick={() => setActiveTab('overview')}
            >
              Over View
            </button>
            <button 
              className={`tab-btn ${activeTab === 'review' ? 'active' : ''}`}
              onClick={() => setActiveTab('review')}
            >
              Review
            </button>
          </div>

          {/* Tab Content */}
          <div className="tab-content">
            {activeTab === 'overview' && (
              <div className="overview-content">
                {/* Product Overview */}
                <div className="overview-section">
                  <h2 className="section-title">Product Overview</h2>
                  <p className="overview-text">
                    Established in 2014, this family-run Italian restaurant is located in a high-footfall downtown area with consistent loyal customer traffic. Fully furnished, with a turnkey setup, trained staff, and high online ratings. Great opportunity for a hands-on operator or investor. Established in 2014, this family-run Italian restaurant is located in a high-footfall downtown area with consistent loyal customer traffic. Fully furnished, with a turnkey setup, trained staff, and high online ratings. Great opportunity for a hands-on operator or investor. Established in 2014, this family-run Italian restaurant is located in a high-footfall downtown area with consistent loyal customer traffic. Fully furnished, with a turnkey setup, trained staff, and high online ratings. Great opportunity for a hands-on operator or investor.
                  </p>
                </div>

                {/* Key Features */}
                <div className="features-section">
                  <h3 className="subsection-title">Key Features</h3>
                  <div className="features-grid">
                    <div className="features-column">
                      {keyFeatures.slice(0, 2).map((feature, index) => (
                        <p key={index} className="feature-item">{feature}</p>
                      ))}
                    </div>
                    <div className="features-column">
                      {keyFeatures.slice(2, 5).map((feature, index) => (
                        <p key={index} className="feature-item">{feature}</p>
                      ))}
                    </div>
                    <div className="features-column">
                      {keyFeatures.slice(5).map((feature, index) => (
                        <p key={index} className="feature-item">{feature}</p>
                      ))}
                    </div>
                  </div>

                  <h3 className="subsection-title">Recommended Use</h3>
                  <div className="recommended-uses">
                    {recommendedUses.map((use, index) => (
                      <p key={index} className="use-item">{use}</p>
                    ))}
                  </div>
                </div>

                {/* Seller's Notes */}
                <div className="seller-notes-section">
                  <h3 className="subsection-title">Seller's Notes</h3>
                  <p className="seller-notes-text">
                    {product?.description || 'Established in 2014, this family-run Italian restaurant is located in a high-footfall downtown area with consistent loyal customer traffic. Fully furnished, with a turnkey setup, trained staff, and high online ratings. Great opportunity for a hands-on operator or investor.'}
                  </p>
                </div>
              </div>
            )}

            {activeTab === 'review' && (
              <div className="review-content">
                {reviews.length > 0 ? (
                  <>
                    <p className="review-count-header">{reviewCount} associated members</p>
                    <div className="reviews-grid">
                      {(() => {
                        const reviewsPerPage = 10;
                        const totalPages = Math.ceil(reviews.length / reviewsPerPage);
                        const startIdx = (currentPage - 1) * reviewsPerPage;
                        const paginatedReviews = reviews.slice(startIdx, startIdx + reviewsPerPage);
                        return (
                          <>
                            {paginatedReviews.map(review => renderReviewCard(review))}
                            
                            <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
                          </>
                        );
                      })()}
                    </div>
                  </>
                ) : (
                  <div className="no-reviews-message" style={{ textAlign: 'center', padding: '60px 20px', color: '#666' }}>
                    <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ margin: '0 auto 16px', display: 'block', opacity: 0.5 }}>
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" fill="#ccc"/>
                    </svg>
                    <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px', color: '#333' }}>No Reviews Yet</h3>
                    <p style={{ fontSize: '14px', color: '#888' }}>This product has not received any reviews.</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Similar Products Section */}
          {relatedProducts.length > 0 && (
          <div className="similar-products-section">
            <div className="similar-products-header">
              <h2 className="similar-products-title">Similar Products</h2>
            </div>
            {(() => {
              const itemsPerRow = 5;
              const rows = [];
              for (let i = 0; i < relatedProducts.length; i += itemsPerRow) {
                rows.push(relatedProducts.slice(i, i + itemsPerRow));
              }
              return rows.map((rowItems, rowIdx) => (
                <div key={rowIdx} className="similar-products-grid">
                  {rowItems.map((item) => (
                <div key={item.id} className="featured-product-card" onClick={() => navigate(`/product/${item.id}`)} style={{ cursor: 'pointer' }}>
                  {/* Product Image Section */}
                  <div className="featured-product-image-section">
                    <img 
                      src={item.image || 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=500'} 
                      alt={item.name} 
                      className="featured-product-img"
                    />
                  </div>
                  
                  {/* Product Details Section */}
                  <div className="featured-product-details-section">
                    {/* Info Block */}
                    <div className="featured-info-block">
                      {/* Product Name Block */}
                      <div className="featured-desc-block">
                        <p className="featured-product-name">{item.name || 'Unknown Product'}</p>
                        
                        {/* Price Block */}
                        <div className="featured-price-block">
                          <div className="featured-price-row">
                            <span className="featured-price-amount">${item.priceMin || 0} - ${item.priceMax || 0} USD</span>
                            <span className="featured-price-unit">/ {item.unit || 'Tons'}</span>
                          </div>
                          <p className="featured-moq">
                            <span className="featured-moq-label">(MOQ) </span>
                            <span className="featured-moq-value">{item.moq || 'N/A'}</span>
                          </p>
                        </div>
                      </div>
                      
                      {/* Badges Block */}
                      <div className="featured-badges-block">
                        <div className="featured-badges-row">
                          {/* Membership Plan Badge */}
                          {(() => {
                            const planConfig = getPlanConfig(item.seller?.membershipPlan);
                            return (
                              <div className="featured-global-badge" style={{ borderColor: planConfig.color }}>
                                <img 
                                  src={planConfig.icon} 
                                  alt={planConfig.label} 
                                  className="featured-global-icon"
                                />
                                <span className="featured-global-text" style={{ color: planConfig.color }}>{planConfig.label}</span>
                              </div>
                            );
                          })()}
                          
                          {/* Verified Badge - only for GCC and Global plans */}
                          {shouldShowVerifiedBadge(item.seller?.membershipPlan) && (
                            <div className="featured-verified-badge">
                              <svg width="16" height="16" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M22 11C22 11.9323 20.7329 12.6871 20.5034 13.5472C20.2653 14.4377 20.9782 15.7221 20.528 16.5015C20.0705 17.2923 18.597 17.314 17.9562 17.9562C17.314 18.5984 17.2923 20.0705 16.5014 20.528C15.7221 20.9782 14.4377 20.2653 13.5472 20.5034C12.6871 20.7329 11.9337 22 11 22C10.0663 22 9.31291 20.7329 8.45276 20.5034C7.56231 20.2653 6.27788 20.9782 5.49856 20.528C4.70769 20.0705 4.68605 18.597 4.04382 17.9562C3.4016 17.3154 1.92954 17.2923 1.47205 16.5015C1.02177 15.7221 1.73472 14.4377 1.49659 13.5472C1.26712 12.6871 0 11.9338 0 11C0 10.0663 1.26712 9.3129 1.49659 8.4528C1.73472 7.5623 1.02177 6.2779 1.47205 5.49857C1.92954 4.7077 3.40305 4.68605 4.04382 4.04383C4.68605 3.40161 4.70769 1.92955 5.49856 1.47206C6.27788 1.02178 7.56231 1.73472 8.45276 1.49659C9.31291 1.26712 10.0663 0 11 0C11.9337 0 12.6871 1.26712 13.5472 1.49659C14.4377 1.73472 15.7221 1.02178 16.5014 1.47206C17.2923 1.92955 17.314 3.40305 17.9562 4.04383C18.5984 4.68461 20.0705 4.7077 20.528 5.49857C20.9782 6.2779 20.2653 7.5623 20.5034 8.4528C20.7329 9.3129 22 10.0663 22 11Z" fill="#29ABE2"/>
                                <path d="M15.5876 7.6015C15.1229 7.1368 14.3696 7.1368 13.9049 7.6015L9.0312 12.4751L7.00786 10.4518C6.54315 9.9871 5.7898 9.9871 5.32509 10.4518C4.86039 10.9165 4.86039 11.6698 5.32509 12.1345L8.14798 14.9574C8.39188 15.2013 8.71372 15.3168 9.0327 15.3052C9.3516 15.3168 9.6734 15.2013 9.9173 14.9574L15.5891 9.2857C16.0538 8.821 16.0538 8.0676 15.5891 7.6029L15.5876 7.6015Z" fill="white"/>
                              </svg>
                              <span className="featured-verified-text">Verified</span>
                            </div>
                          )}
                        </div>
                        
                        {/* Seller Company Name */}
                        <div 
                          className="featured-seller-company"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (item.seller?.id) navigate(`/seller/${item.seller.id}`);
                          }}
                          style={{ cursor: item.seller?.id ? 'pointer' : 'default' }}
                        >
                          <img 
                            src={item.seller?.logo || 'https://via.placeholder.com/40x40?text=Logo'} 
                            alt={item.seller?.name || 'Company'} 
                            className="featured-company-logo"
                          />
                          <span className="featured-company-name">{item.seller?.name || item.seller?.companyName || 'Company'}</span>
                        </div>
                        
                        {/* Location Row */}
                        <div className="featured-location-row">
                          <div className="featured-flag-wrapper">
                            <img 
                              src={item.seller?.countryFlag || 'https://flagcdn.com/w80/ae.png'} 
                              alt="Flag" 
                              className="featured-flag-img"
                            />
                          </div>
                          <span className="featured-location-text">
                            <span className="featured-country">{(item.seller?.country || 'UAE').replace('United Arab Emirates', 'UAE')} / </span>
                            <span className="featured-years">{item.seller?.memberYears || 1} Year{(item.seller?.memberYears || 1) > 1 ? 's' : ''}</span>
                          </span>
                        </div>
                        
                        {/* Business Type */}
                        <span className="featured-business-text">{item.seller?.businessType || 'Manufacturer'}</span>
                      </div>
                    </div>
                    
                    {/* Action Buttons */}
                    <div className="featured-actions-row">
                      <button 
                        className={`featured-btn-inquire ${isInInquiry(item.id) ? 'added' : ''}`}
                        onClick={(e) => handleSimilarInquireClick(item, e)}
                      >
                        {addedProducts[item.id] === true ? '\u2713 Added!' : isInInquiry(item.id) ? 'Remove' : 'Add To Quotation'}
                      </button>
                      <button 
                        className="featured-btn-call"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          const phone = item.seller?.phone || item.seller?.phoneNumber || item.phone;
                          if (phone) {
                            window.location.href = `tel:${phone}`;
                          } else {
                            alert('Phone number not available for this seller');
                          }
                        }}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M22 16.92V19.92C22 20.4704 21.7893 20.9981 21.4142 21.3981C21.0391 21.7982 20.5304 22.0362 19.98 22.0699C19.643 22.0899 19.2989 22.0999 18.95 22.0999C10.21 22.0999 3.10001 14.9899 3.10001 6.24994C3.10001 5.90094 3.11001 5.55694 3.13001 5.21994C3.16376 4.66947 3.40177 4.16077 3.80183 3.78568C4.20189 3.41059 4.72954 3.19994 5.28001 3.19994H8.28001C8.72424 3.19557 9.15598 3.35553 9.49332 3.64969C9.83067 3.94386 10.0507 4.35171 10.11 4.79194C10.2199 5.66094 10.4293 6.51394 10.73 7.32994C10.8567 7.66661 10.8837 8.03161 10.808 8.38227C10.7323 8.73294 10.5571 9.05427 10.3 9.30994L9.00001 10.6099C10.3612 13.1755 12.4245 15.2387 14.99 16.5999L16.29 15.2999C16.5457 15.0429 16.8671 14.8677 17.2177 14.792C17.5684 14.7163 17.9334 14.7433 18.27 14.8699C19.086 15.1707 19.9391 15.3801 20.808 15.4899C21.2532 15.5498 21.6651 15.7746 21.9605 16.1183C22.256 16.462 22.4131 16.9009 22.4 17.3499L22 16.92Z" stroke="#6D6D6D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        <span>Call</span>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
                </div>
              ));
            })()}
          </div>
          )}
        </>
        )}
        </div>
      </main>
      
      <Footer />
      
      {/* Chat Modal */}
      <ChatModal
        isOpen={isChatModalOpen}
        onClose={() => setIsChatModalOpen(false)}
        seller={{
          id: product?.seller?.id || seller.id,
          name: seller.name || seller.companyName,
          logo: seller.logo,
          isVerified: seller.isVerified
        }}
      />

      {/* Upgrade Prompt Modal */}
      <UpgradePromptModal
        isOpen={showUpgradeModal}
        onClose={() => {
          setShowUpgradeModal(false);
          setUpgradeMessage('');
        }}
        feature={upgradeFeature}
        message={upgradeMessage}
        currentPlan={planDisplayName || 'Basic'}
        requiredPlan="GCC Business"
      />

      {/* Login Modal */}
      <LoginModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        onSwitchToRegister={() => setShowLoginModal(false)}
      />
    </div>
  );
};

export default ProductDetailsPage;
